import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image
} from "@react-pdf/renderer";
import JsBarcode from "jsbarcode";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const styles = StyleSheet.create({
  page: { padding: 25, fontSize: 9, fontFamily: "Helvetica" },
  headerRow: { flexDirection: "row", justifyContent: "space-between", marginBottom: 15 },
  leftHeader: { width: "60%" },
  rightHeader: { width: "40%", textAlign: "right" },
  bold: { fontWeight: "bold" },
  academicYear: { fontWeight: "bold", marginBottom: 15 },
  row: { flexDirection: "row", marginTop: 5 },
  separator: { height: 2, backgroundColor: "#007BFF", marginVertical: 15 },
  noteChiffreBox: { border: "1 solid black", height: 40, width: "50%", alignSelf: "center", justifyContent: "center", alignItems: "center", flexDirection: "column" },
  notAvailableBox: { border: "1 solid black", height: 40, width: "50%", alignSelf: "center", justifyContent: "center", alignItems: "center", flexDirection: "column" },
  appreciationBox: { border: "1 solid black", height: 60, width: "50%", alignSelf: "center", justifyContent: "flex-start", alignItems: "center", paddingTop: 5, flexDirection: "column" },
  gridRow: { flexDirection: "row" },
  gridCell: { border: "1 solid black", width: 20, height: 20 }
});

// 🔹 Pré-génère les codes-barres pour tous les records
const generateBarcodeDataUrls = (records) => {
  return records.map(record => {
    if (record.barcode) {
      const canvas = document.createElement("canvas");
      JsBarcode(canvas, record.barcode, { format: "CODE128", width: 2, height: 50, displayValue: false });
      return canvas.toDataURL("image/png");
    }
    return null;
  });
};

const MultipleFichesPDF = ({ records }) => {
  const barcodeDataUrls = generateBarcodeDataUrls(records);

  return (
    <Document>
      {records.map((record, index) => {
        const barcodeDataUrl = barcodeDataUrls[index];

        return (
          <Page key={index} size="A4" style={styles.page}>

            {/* ================== HEADER ================== */}
            <View style={styles.headerRow}>
              <View style={styles.leftHeader}>
                <Text>UNIVERSITE DES SCIENCES, DES TECHNIQUES</Text>
                <Text>ET DES TECHNOLOGIES DE BAMAKO : USTTB</Text>
                <Text>FACULTE DE MEDECINE ET D'ODONTO-STOMATOLOGIE : FMOS</Text>
              </View>
              <View style={styles.rightHeader}>
                <Text>République du Mali</Text>
                <Text>Un Peuple - Un But - Une Foi</Text>
              </View>
            </View>

            <View style={styles.rightHeader}>
              <Text style={styles.academicYear}>
                Année universitaire : {record.academic_year?.name || "2024/2025"}
              </Text>
            </View>

            <View style={{ flexDirection: "row", marginBottom: 5 }}>
              <Text style={{ fontWeight: "bold" }}> Classe de :  </Text>
              <Text style={{ marginRight: 10 }}>{record.study_level?.name || "-"} </Text>
              <Text style={{ fontWeight: "bold" }}> Examen de : </Text>
              {/*<Text style={{ marginRight: 10 }}>{record.evaluation?.title || "-"} </Text>*/}
              <Text style={{ marginRight: 10 }}>{record.course_component?.title || "-"} </Text>
              <Text style={{ fontWeight: "bold" }}>Session : </Text>
              <Text style={{ marginRight: 20 }}>{record.student?.session || "-"}</Text>
              <Text style={{ fontWeight: "bold" }}>Partiel: </Text>
              <Text style={{ marginRight: 20 }}>{record.student?.session || "-"}</Text>
            </View>

            <View style={{ flexDirection: "row", marginBottom: 5 }}>
              <Text style={{ fontWeight: "bold" }}> N°Etudiant : </Text>
              <Text style={{ marginRight: 10 }}>{record.student?.student_number || "-"}</Text>
              <Text style={{ fontWeight: "bold" }}> Nom :  </Text>
              <Text style={{ marginRight: 10 }}>{record.student?.last_name || "-"}</Text>
              <Text style={{ fontWeight: "bold" }}>Prénoms :</Text>
              <Text style={{ marginRight: 10 }}> {record.student?.first_name || "-"} </Text>
            </View>

            <View style={styles.row}>
              <Text style={{ fontWeight: "bold" }}>Date de Naissance :  </Text>
              <Text style={{ marginRight: 10 }}>{record.student?.birth_date ? format(new Date(record.student.birth_date), "dd/MM/yyyy", { locale: fr }) : "-"}</Text>
              <Text style={{ fontWeight: "bold" }}>Crédit: </Text>
              <Text style={{ marginRight: 10 }}> {"" || "-"} </Text>
              <Text style={{ fontWeight: "bold" }}>Classe crédit: </Text>
              <Text style={{ marginRight: 10 }}>{"" || "-"}</Text>
            </View>

            {/* 🔹 CODE-BARRES GRAPHIC - EN HAUT */}
            {barcodeDataUrl && (
              <View style={{ alignItems: "center", marginVertical: 10 }}>
                <Image src={barcodeDataUrl} style={{ width: 200, height: 50 }} />
              </View>
            )}

            {/* LIGNE BLEUE DE SEPARATION */}
            <View style={styles.separator} />

            {/* Classe à gauche, Évaluation à droite */}
            <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 10 }}>
              <Text>{record.study_level?.name || "-"}</Text>
              {/*<Text>{record.evaluation?.title || "-"}</Text>*/}
              <Text style={{ marginRight: 10 }}>{record.course_component?.title || "-"} </Text>
            </View>

            {/* 🔹 CODE-BARRES RÉPLIQUÉ APRÈS LA LIGNE */}
            {barcodeDataUrl && (
              <View style={{ alignItems: "center", marginVertical: 10 }}>
                <Image src={barcodeDataUrl} style={{ width: 200, height: 50 }} />
              </View>
            )}

            {/* 🔹 TEXTE SOUS LE SECOND CODE-BARRES, aligné à gauche */}
            <Text style={{ alignSelf: "flex-start", marginBottom: 10 }}>
              Remettre cette page avec vos copies de composition
            </Text>

            {/* ================== PARTIE BAS ================== */}
            <View style={styles.appreciationBox}>
              <Text style={{ fontWeight: "bold", marginBottom: 5 }}>Appréciations du Professeur</Text>
              <Text>{record.teacher_comment || ""}</Text>
            </View>

            {/* 🔹 Répétitions exactes */}
            <View style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 5, marginBottom: 15 }}>
              <Text>Ne rien écrire sur cette page</Text>
              <Text>Ne rien écrire sur cette page</Text>
            </View>

            <View style={{ marginTop: 0 }}>
              <View style={styles.noteChiffreBox}>
                <Text style={{ fontWeight: "bold" }}>Note chiffrée</Text>
              </View>
              <Text style={{ alignSelf: "flex-end", marginTop: 15 }}>
                Remettre cette page avec vos copies de composition
              </Text>
            </View>

            {barcodeDataUrl && (
              <View style={{ alignItems: "center", marginVertical: 10 }}>
                <Image src={barcodeDataUrl} style={{ width: 200, height: 50 }} />
              </View>
            )}

            <View style={{ marginTop: 0 }}>
              <View style={styles.notAvailableBox}>
                <Text style={{ fontWeight: "bold" }}>NA</Text>
              </View>
            </View>

            <View style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 5, marginBottom: 15 }}>
              <Text>Ne rien écrire sur cette page</Text>
              <Text>Remettre cette page avec vos copies de composition</Text>
            </View>

            {barcodeDataUrl && (
              <View style={{ alignItems: "center", marginVertical: 10 }}>
                <Image src={barcodeDataUrl} style={{ width: 200, height: 50 }} />
              </View>
            )}

            <View style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 5, marginBottom: 15 }}>
              <Text>Ne rien écrire sur cette page</Text>
              <Text>Remettre cette page avec vos copies de composition</Text>
            </View>

            <View style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 5, marginBottom: 15 }}>
              <Text>Ne rien écrire sur cette page</Text>
              <Text>Remettre cette page avec vos copies de composition</Text>
            </View>

          </Page>
        );
      })}
    </Document>
  );
};

export default MultipleFichesPDF;